import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import emailjs from '@emailjs/browser'

// Matches --primary in assets/css/style.css. Set explicitly because the themed
// bootstrap build renders .btn-primary text as #000, which is unreadable on green.
const BRAND_GREEN = '#1F5839'

const tabStyle = (isActive) => ({
  borderRadius: '30px',
  backgroundColor: isActive ? BRAND_GREEN : 'transparent',
  color: isActive ? '#FFFFFF' : BRAND_GREEN,
  border: `2px solid ${isActive ? BRAND_GREEN : 'transparent'}`,
  fontWeight: 600
})

// International packages (source: Holiday Royal international tour brochure)
const internationalPackages = [
  {
    id: 'vietnam-grand-tour-11n-12d',
    title: 'Vietnam Grand Tour',
    location: 'Vietnam',
    duration: '12 days',
    image: 'assets/img/vietnam.jpg',
    price: '₹1,19,000',
    description: 'Hanoi, Halong Bay, Da Nang, Ho Chi Minh & Phu Quoc — end-to-end Vietnam with all meals included',
    titleSize: '1.1rem'
  },
  {
    id: 'malaysia-3n-4d',
    title: 'Unbeatable Malaysia',
    location: 'Malaysia',
    duration: '4 days',
    image: 'assets/img/malaysia.jpg',
    price: '₹60,000',
    description: 'Kuala Lumpur city, Genting Highlands, Batu Caves and Sunway Lagoon Night Park',
    titleSize: '1.1rem'
  },
  {
    id: 'japan-classic-route-6n-7d',
    title: 'Japan for First Timers',
    location: 'Japan',
    duration: '7 days',
    image: 'assets/img/japan.jpg',
    price: '₹3,60,000',
    description: 'The Classic Golden Route — Tokyo, Mount Fuji, Kanazawa, Kyoto & Osaka by Bullet Train',
    titleSize: '1.1rem'
  },
  {
    id: 'singapore-malaysia-6n-7d',
    title: 'Unbelievable Singapore & Malaysia',
    location: 'Singapore & Malaysia',
    duration: '7 days',
    image: 'assets/img/singapore.jpg',
    price: '₹1,60,000',
    description: 'Universal Studios, Sentosa Island, Gardens by the Bay, Genting Highlands & KL Tower',
    titleSize: '1rem'
  },
  {
    id: 'essence-of-europe-7n-8d',
    title: 'Essence of Europe',
    location: 'Italy & Switzerland',
    duration: '8 days',
    image: 'assets/img/europe.jpg',
    price: '₹2,65,000',
    description: 'Rome, Vatican, Pisa, Florence, Venice gondola ride, Milan, Lake Como & Mt. Titlis',
    titleSize: '1.1rem'
  },
  {
    id: 'amazing-bhutan-7n-8d',
    title: 'Amazing Bhutan',
    location: 'Bhutan',
    duration: '8 days',
    image: 'assets/img/bhutan.jpg',
    price: '₹40,000',
    description: "Thimphu, Punakha, Paro with Tiger's Nest excursion, Dochula Pass and Chele La Pass",
    titleSize: '1.1rem'
  }
]

function InternationalPackageCard({ pkg, delay }) {
  return (
    <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay={delay}>
      <div className="package-item">
        <div className="overflow-hidden">
          <img className="img-fluid" src={pkg.image} alt={pkg.title} />
        </div>
        <div className="d-flex border-bottom">
          <small className="flex-fill text-center border-end py-2">
            <i className="fa fa-map-marker-alt text-primary me-2" />
            {pkg.location}
          </small>
          <small className="flex-fill text-center border-end py-2">
            <i className="fa fa-calendar-alt text-primary me-2" />
            {pkg.duration}
          </small>
          <small className="flex-fill text-center py-2">
            <i className="fa fa-user text-primary me-2" />
            Per Person
          </small>
        </div>
        <div className="text-center p-4">
          <h3 className="mb-0 text-primary" style={{ fontSize: '1.4rem', fontWeight: '700' }}>
            {pkg.price}
          </h3>
          <h4 className="mb-0 mt-2" style={{ fontSize: pkg.titleSize, fontWeight: '600' }}>
            {pkg.title}
          </h4>
          <div className="mb-3">
            <small className="fa fa-star text-primary" />
            <small className="fa fa-star text-primary" />
            <small className="fa fa-star text-primary" />
            <small className="fa fa-star text-primary" />
            <small className="fa fa-star text-primary" />
          </div>
          <p>{pkg.description}</p>
          <div className="d-flex justify-content-center gap-2 mb-2">
            <Link
              to={`/package/${pkg.id}`}
              className="btn btn-sm btn-outline-primary px-4"
              style={{ borderRadius: '30px' }}
            >
              View Details
            </Link>
            <a
              href="#book-appointment"
              className="btn btn-sm btn-primary px-4"
              style={{ borderRadius: '30px' }}
            >
              Book Now
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

function Packages() {
  const [activeTab, setActiveTab] = useState('indian')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' })

  // EmailJS Configuration
  const EMAILJS_SERVICE_ID = 'service_6c28bw8'
  const EMAILJS_TEMPLATE_ID = 'template_nzbpjvv'
  const EMAILJS_PUBLIC_KEY = 'yBsx7RgM1xpQ3rZog'

  const handleInputChange = (e) => {
    const { id, value } = e.target
    setFormData(prev => ({
      ...prev,
      [id]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus({ type: '', message: '' })

    // Validate form
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus({ type: 'error', message: 'Please fill in all fields.' })
      setIsSubmitting(false)
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({ type: 'error', message: 'Please enter a valid email address.' })
      setIsSubmitting(false)
      return
    }

    try {
      // Prepare email template parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone || 'Not provided',
        message: formData.message,
        to_email: 'ankitmandli2001@gmail.com',
        subject: 'New Booking Appointment -  Holiday Royal'
      }

      // Send email using EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      )

      setSubmitStatus({ 
        type: 'success', 
        message: 'Thank you! Your booking request has been sent successfully. We will contact you soon.' 
      })
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      })
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: `Sorry, there was an error: ${error.text || error.message || 'Please try again later or contact us directly.'}` 
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
          <div className="container-fluid bg-primary py-5 mb-5 hero-header">
    <div className="container py-5">
      <div className="row justify-content-center py-5">
        <div className="col-lg-10 pt-lg-5 mt-lg-5 text-center">
          <h1 className="display-3 text-white animated slideInDown">
            Packages
          </h1>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb justify-content-center">
              <li className="breadcrumb-item">
                <a href="/">Home</a>
              </li>
              <li
                className="breadcrumb-item text-white active"
                aria-current="page"
              >
                Packages
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
      {/* Package Start */}
      <div className="container-xxl py-5">
      <div className="container">
        <div className="text-center wow fadeInUp" data-wow-delay="0.1s">
          <h6 className="section-title bg-white text-center text-primary px-3">
            Packages
          </h6>
          <h1 className="mb-4">Awesome Packages</h1>
        </div>

        {/* Tabs: Indian / International */}
        <div className="d-flex justify-content-center mb-5 wow fadeInUp" data-wow-delay="0.2s">
          <div className="d-inline-flex flex-wrap justify-content-center gap-2 p-2 bg-light" style={{ borderRadius: '50px' }}>
            <button
              type="button"
              className="btn px-4 py-2"
              style={tabStyle(activeTab === 'indian')}
              onClick={() => setActiveTab('indian')}
            >
              <i className="fa fa-map-marked-alt me-2" />
              Indian Packages
            </button>
            <button
              type="button"
              className="btn px-4 py-2"
              style={tabStyle(activeTab === 'international')}
              onClick={() => setActiveTab('international')}
            >
              <i className="fa fa-globe-asia me-2" />
              International Packages
            </button>
          </div>
        </div>

        {/* International Packages */}
        {activeTab === 'international' && (
          <div className="row g-4 justify-content-center">
            {internationalPackages.map((pkg, index) => (
              <InternationalPackageCard
                key={pkg.id}
                pkg={pkg}
                delay={`${0.1 * ((index % 3) + 1)}s`}
              />
            ))}
          </div>
        )}

        {/* Indian Packages */}
        <div className={`row g-4 justify-content-center ${activeTab === 'indian' ? '' : 'd-none'}`}>
          {/* Rajasthan Package */}
          <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay="0.1s">
            <div className="package-item">
              <div className="overflow-hidden">
                <img className="img-fluid" src="assets/img/rajasthan.jpg" alt="Rajasthan Tour Package" />
              </div>
              <div className="d-flex border-bottom">
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-map-marker-alt text-primary me-2" />
                  Rajasthan
                </small>
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-calendar-alt text-primary me-2" />6 days
                </small>
                <small className="flex-fill text-center py-2">
                  <i className="fa fa-user text-primary me-2" />2 Person
                </small>
              </div>
              <div className="text-center p-4">
                <h4 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '600' }}>Rajasthan Tour Package</h4>
                <div className="mb-3">
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                </div>
                <p>
                  Discover the royal palaces, majestic forts, and vibrant culture of the Land of Kings
                </p>
                <div className="d-flex justify-content-center gap-2 mb-2">
                  <Link
                    to="/package/rajasthan-5n-6d"
                    className="btn btn-sm btn-outline-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    View Details
                  </Link>
                  <a
                    href="#book-appointment"
                    className="btn btn-sm btn-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Assam Meghalaya Arunachal Package */}
          <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay="0.2s">
            <div className="package-item">
              <div className="overflow-hidden">
                <img className="img-fluid" src="assets/img/darjeeling.jpg" alt="North East Tour Package" />
              </div>
              <div className="d-flex border-bottom">
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-map-marker-alt text-primary me-2" />
                  North East
                </small>
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-calendar-alt text-primary me-2" />9 days
                </small>
                <small className="flex-fill text-center py-2">
                  <i className="fa fa-user text-primary me-2" />2 Person
                </small>
              </div>
              <div className="text-center p-4">
                <h4 className="mb-0" style={{ fontSize: '1rem', fontWeight: '600' }}>Assam - Meghalaya - Arunachal Pradesh Tour</h4>
                <div className="mb-3">
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                </div>
                <p>
                  Explore the pristine beauty of Assam, Meghalaya, and Arunachal Pradesh
                </p>
                <div className="d-flex justify-content-center gap-2 mb-2">
                  <Link
                    to="/package/assam-meghalaya-arunachal-8n-9d"
                    className="btn btn-sm btn-outline-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    View Details
                  </Link>
                  <a
                    href="#book-appointment"
                    className="btn btn-sm btn-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Kashmir Package */}
          <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay="0.3s">
            <div className="package-item">
              <div className="overflow-hidden">
                <img className="img-fluid" src="assets/img/himachal.jpg" alt="Kashmir Tour Package" />
              </div>
              <div className="d-flex border-bottom">
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-map-marker-alt text-primary me-2" />
                  Kashmir
                </small>
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-calendar-alt text-primary me-2" />6 days
                </small>
                <small className="flex-fill text-center py-2">
                  <i className="fa fa-user text-primary me-2" />2 Person
                </small>
              </div>
              <div className="text-center p-4">
                <h4 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '600' }}>Kashmir Tour Package</h4>
                <div className="mb-3">
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                </div>
                <p>
                  Experience the paradise on earth with houseboats, gardens, and snow-capped mountains
                </p>
                <div className="d-flex justify-content-center gap-2 mb-2">
                  <Link
                    to="/package/kashmir-5n-6d"
                    className="btn btn-sm btn-outline-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    View Details
                  </Link>
                  <a
                    href="#book-appointment"
                    className="btn btn-sm btn-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Ladakh Package */}
          <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay="0.4s">
            <div className="package-item">
              <div className="overflow-hidden">
                <img className="img-fluid" src="assets/img/himachal-pack.jpg" alt="Ladakh Tour Package" />
              </div>
              <div className="d-flex border-bottom">
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-map-marker-alt text-primary me-2" />
                  Ladakh
                </small>
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-calendar-alt text-primary me-2" />8 days
                </small>
                <small className="flex-fill text-center py-2">
                  <i className="fa fa-user text-primary me-2" />2 Person
                </small>
              </div>
              <div className="text-center p-4">
                <h4 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '600' }}>Ladakh Tour Package</h4>
                <div className="mb-3">
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                </div>
                <p>
                  Explore the land of high passes, pristine lakes, and Buddhist monasteries
                </p>
                <div className="d-flex justify-content-center gap-2 mb-2">
                  <Link
                    to="/package/ladakh-7n-8d"
                    className="btn btn-sm btn-outline-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    View Details
                  </Link>
                  <a
                    href="#book-appointment"
                    className="btn btn-sm btn-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Delhi Agra Package */}
          <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay="0.5s">
            <div className="package-item">
              <div className="overflow-hidden">
                <img className="img-fluid" src="assets/img/agra.jpg" alt="Delhi Agra Tour Package" />
              </div>
              <div className="d-flex border-bottom">
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-map-marker-alt text-primary me-2" />
                  Delhi & Agra
                </small>
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-calendar-alt text-primary me-2" />4 days
                </small>
                <small className="flex-fill text-center py-2">
                  <i className="fa fa-user text-primary me-2" />2 Person
                </small>
              </div>
              <div className="text-center p-4">
                <h4 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '600' }}>Delhi - Agra Tour Package</h4>
                <div className="mb-3">
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                </div>
                <p>
                  Visit the iconic Taj Mahal, Agra Fort, and explore the capital city of India
                </p>
                <div className="d-flex justify-content-center gap-2 mb-2">
                  <Link
                    to="/package/delhi-agra-3n-4d"
                    className="btn btn-sm btn-outline-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    View Details
                  </Link>
                  <a
                    href="#book-appointment"
                    className="btn btn-sm btn-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Manali Kasol Package */}
          <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay="0.6s">
            <div className="package-item">
              <div className="overflow-hidden">
                <img className="img-fluid" src="assets/img/manali.jpg" alt="Manali Kasol Tour Package" />
              </div>
              <div className="d-flex border-bottom">
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-map-marker-alt text-primary me-2" />
                  Manali & Kasol
                </small>
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-calendar-alt text-primary me-2" />5 days
                </small>
                <small className="flex-fill text-center py-2">
                  <i className="fa fa-user text-primary me-2" />2 Person
                </small>
              </div>
              <div className="text-center p-4">
                <h4 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '600' }}>Manali - Kasol Tour Package</h4>
                <div className="mb-3">
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                </div>
                <p>
                  Experience the beauty of mountains, valleys, and adventure activities
                </p>
                <div className="d-flex justify-content-center gap-2 mb-2">
                  <Link
                    to="/package/manali-kasol-4n-5d"
                    className="btn btn-sm btn-outline-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    View Details
                  </Link>
                  <a
                    href="#book-appointment"
                    className="btn btn-sm btn-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Pachmarhi Package */}
          <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay="0.7s">
            <div className="package-item">
              <div className="overflow-hidden">
                <img className="img-fluid" src="assets/img/himachal.jpg" alt="Kashmir Tour Package" />
              </div>
              <div className="d-flex border-bottom">
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-map-marker-alt text-primary me-2" />
                  Pachmarhi
                </small>
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-calendar-alt text-primary me-2" />3 days
                </small>
                <small className="flex-fill text-center py-2">
                  <i className="fa fa-user text-primary me-2" />2 Person
                </small>
              </div>
              <div className="text-center p-4">
                <h4 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '600' }}>Pachmarhi Tour Package</h4>
                <div className="mb-3">
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                </div>
                <p>
                  Explore the only hill station of Madhya Pradesh with waterfalls and caves
                </p>
                <div className="d-flex justify-content-center gap-2 mb-2">
                  <Link
                    to="/package/pachmarhi-2n-3d"
                    className="btn btn-sm btn-outline-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    View Details
                  </Link>
                  <a
                    href="#book-appointment"
                    className="btn btn-sm btn-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bhutan Package */}
          <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay="0.1s">
            <div className="package-item">
              <div className="overflow-hidden">
                <img className="img-fluid" src="assets/img/himachal-pack.jpg" alt="Ladakh Tour Package" />
              </div>
              <div className="d-flex border-bottom">
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-map-marker-alt text-primary me-2" />
                  Bhutan
                </small>
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-calendar-alt text-primary me-2" />7 days
                </small>
                <small className="flex-fill text-center py-2">
                  <i className="fa fa-user text-primary me-2" />2 Person
                </small>
              </div>
              <div className="text-center p-4">
                <h4 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '600' }}>Bhutan Tour Package</h4>
                <div className="mb-3">
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                </div>
                <p>
                  Experience the land of happiness with monasteries, fortresses, and pristine nature
                </p>
                <div className="d-flex justify-content-center gap-2 mb-2">
                  <Link
                    to="/package/bhutan-6n-7d"
                    className="btn btn-sm btn-outline-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    View Details
                  </Link>
                  <a
                    href="#book-appointment"
                    className="btn btn-sm btn-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Udaipur Jodhpur Jaisalmer Package */}
          <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay="0.2s">
            <div className="package-item">
              <div className="overflow-hidden">
                <img className="img-fluid" src="assets/img/rajasthan.jpg" alt="Rajasthan Tour Package" />
              </div>
              <div className="d-flex border-bottom">
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-map-marker-alt text-primary me-2" />
                  Rajasthan
                </small>
                <small className="flex-fill text-center border-end py-2">
                  <i className="fa fa-calendar-alt text-primary me-2" />Custom
                </small>
                <small className="flex-fill text-center py-2">
                  <i className="fa fa-user text-primary me-2" />2 Person
                </small>
              </div>
              <div className="text-center p-4">
                <h4 className="mb-0" style={{ fontSize: '1rem', fontWeight: '600' }}>Udaipur - Jodhpur - Jaisalmer Tour</h4>
                <div className="mb-3">
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                  <small className="fa fa-star text-primary" />
                </div>
                <p>
                  Explore the royal cities of Udaipur, Jodhpur, and Jaisalmer
                </p>
                <div className="d-flex justify-content-center gap-2 mb-2">
                  <Link
                    to="/package/udaipur-jodhpur-jaisalmer"
                    className="btn btn-sm btn-outline-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    View Details
                  </Link>
                  <a
                    href="#book-appointment"
                    className="btn btn-sm btn-primary px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>
          </div>
       
        </div>
      </div>
    </div>
    {/* Package End */}
    {/* Booking Start */}
    <div id="book-appointment" className="container-xxl py-5 wow fadeInUp" data-wow-delay="0.1s">
      <div className="container">
        <div className="booking p-5">
          <div className="row g-5 align-items-center">
            <div className="col-md-6 text-white">
              <h6 className="text-white text-uppercase">Booking</h6>
              <h1 className="text-white mb-4">Book Appointment</h1>
              <p className="mb-4">
                Book your dream vacation with Holiday Royal through our convenient online booking system. Choose from hundreds of destinations and packages tailored to your preferences.
              </p>
              <p className="mb-4">
                Our easy booking process allows you to reserve your trip in just a few clicks. Get instant confirmations, flexible payment options, and 24/7 customer support throughout your journey.
              </p>
              <button className="btn btn-outline-light py-3 px-5 mt-2" type="button">
                Read More
              </button>
            </div>
            <div className="col-md-6">
              <h1 className="text-white mb-4">Book Appointment</h1>
              {submitStatus.message && (
                <div 
                  className={`alert ${submitStatus.type === 'success' ? 'alert-success' : 'alert-danger'} mb-4`}
                  role="alert"
                >
                  {submitStatus.message}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        type="text"
                        className="form-control bg-transparent text-white"
                        id="name"
                        placeholder="Your Name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                      <label htmlFor="name" className="text-white">Your Name</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        type="email"
                        className="form-control bg-transparent text-white"
                        id="email"
                        placeholder="Your Email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                      <label htmlFor="email" className="text-white">Your Email</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        type="tel"
                        className="form-control bg-transparent text-white"
                        id="phone"
                        placeholder="Your Phone Number"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                      <label htmlFor="phone" className="text-white">Your Phone Number</label>
                    </div>
                  </div>
             
                  <div className="col-12">
                    <div className="form-floating">
                      <textarea
                        className="form-control bg-transparent text-white"
                        placeholder="Message"
                        id="message"
                        style={{ height: 100 }}
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                      />
                      <label htmlFor="message" className="text-white">Message</label>
                    </div>
                  </div>
                  <div className="col-12">
                    <button
                      className="btn btn-outline-light w-100 py-3"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Book Now'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
    {/* Booking Start */}
    {/* Process Start */}
    <div className="container-xxl py-5">
      <div className="container">
        <div className="text-center pb-4 wow fadeInUp" data-wow-delay="0.1s">
          <h6 className="section-title bg-white text-center text-primary px-3">
            Process
          </h6>
          <h1 className="mb-5">3 Easy Steps</h1>
        </div>
        <div className="row gy-5 gx-4 justify-content-center">
          <div
            className="col-lg-4 col-sm-6 text-center pt-4 wow fadeInUp"
            data-wow-delay="0.1s"
          >
            <div className="position-relative border border-primary pt-5 pb-4 px-4">
              <div
                className="d-inline-flex align-items-center justify-content-center bg-primary rounded-circle position-absolute top-0 start-50 translate-middle shadow"
                style={{ width: 100, height: 100 }}
              >
                <i className="fa fa-globe fa-3x text-white" />
              </div>
              <h5 className="mt-4">Choose A Destination</h5>
              <hr className="w-25 mx-auto bg-primary mb-1" />
              <hr className="w-50 mx-auto bg-primary mt-0" />
              <p className="mb-0">
                Browse our extensive collection of destinations and find the perfect location for your next adventure
              </p>
            </div>
          </div>
          <div
            className="col-lg-4 col-sm-6 text-center pt-4 wow fadeInUp"
            data-wow-delay="0.3s"
          >
            <div className="position-relative border border-primary pt-5 pb-4 px-4">
              <div
                className="d-inline-flex align-items-center justify-content-center bg-primary rounded-circle position-absolute top-0 start-50 translate-middle shadow"
                style={{ width: 100, height: 100 }}
              >
                <i className="fa fa-dollar-sign fa-3x text-white" />
              </div>
              <h5 className="mt-4">Pay Online</h5>
              <hr className="w-25 mx-auto bg-primary mb-1" />
              <hr className="w-50 mx-auto bg-primary mt-0" />
              <p className="mb-0">
                Browse our extensive collection of destinations and find the perfect location for your next adventure
              </p>
            </div>
          </div>
          <div
            className="col-lg-4 col-sm-6 text-center pt-4 wow fadeInUp"
            data-wow-delay="0.5s"
          >
            <div className="position-relative border border-primary pt-5 pb-4 px-4">
              <div
                className="d-inline-flex align-items-center justify-content-center bg-primary rounded-circle position-absolute top-0 start-50 translate-middle shadow"
                style={{ width: 100, height: 100 }}
              >
                <i className="fa fa-plane fa-3x text-white" />
              </div>
              <h5 className="mt-4">Fly Today</h5>
              <hr className="w-25 mx-auto bg-primary mb-1" />
              <hr className="w-50 mx-auto bg-primary mt-0" />
              <p className="mb-0">
                Browse our extensive collection of destinations and find the perfect location for your next adventure
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    {/* Process Start */}
    </div>
  )
}

export default Packages
